import { Helmet } from 'react-helmet-async';

const SEOHead = ({ 
  title = "Freys Media - Videoproduktion & Filmemacher im Schwarzwald | Kenny Frey",
  description = "Videoproduktion im Schwarzwald: Social Media Content, Imagefilme, Automotive- und Recruiting-Videos. Dazu KI-Content über die eigene Plattform freycreate.ai und Websites — deutschlandweit remote. Kenny Frey aus Villingen-Schwenningen.",
  keywords = "Videoproduktion Schwarzwald, Videograf Baden-Württemberg, Social Media Content Creator, Instagram Reel Produktion, Filmemacher Schwarzwald, Imagefilm Unternehmen, Automotive Videoproduktion, Kameramann Schwarzwald, Content Creator Baden-Württemberg, Freycreate AI, KI Video Generation",
  ogImage = "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/0uxu2fq0-kenny-profilbild.jpg",
  ogType = "website",
  canonicalUrl = "https://freysmedia.de"
}) => {
  
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "VideoCreationService",
        "name": "Freys Media",
        "description": "Professionelle Videoproduktion und Content Creation im Schwarzwald",
        "url": "https://freysmedia.de",
        "logo": "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev/images/0uxu2fq0-kenny-profilbild.jpg",
        "image": ogImage,
        "founder": {
          "@type": "Person",
          "name": "Kenny Frey",
          "jobTitle": "Videograf & Content Creator",
          "description": "Leidenschaftlicher Filmemacher mit Fokus auf Social Media Content, Automotive-Videoproduktion und KI-gestützte Content-Erstellung"
        },
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Villingen-Schwenningen",
          "addressRegion": "Baden-Württemberg",
          "addressCountry": "DE"
        },
        "areaServed": [
          {
            "@type": "City",
            "name": "Villingen-Schwenningen"
          },
          {
            "@type": "State", 
            "name": "Baden-Württemberg"
          },
          {
            "@type": "Country",
            "name": "Deutschland"
          }
        ],
        "serviceType": [
          "Videoproduktion",
          "Social Media Content Creation",
          "Imagefilm Produktion",
          "Automotive Videografie",
          "Recruiting Videos",
          "Instagram Reel Produktion",
          "KI-gestützte Content-Erstellung",
          "Hochzeitsvideos",
          "Musikvideos"
        ],
        "knowsAbout": [
          "Videoproduktion",
          "Content Creation",
          "Social Media Marketing",
          "Cinematography",
          "KI Video Generation",
          "Automotive Videografie"
        ]
      },
      {
        "@type": "WebSite",
        "name": "Freys Media",
        "url": "https://freysmedia.de",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://freysmedia.de/?s={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "LocalBusiness",
        "name": "Freys Media - Kenny Frey Videoproduktion",
        "image": ogImage,
        "@id": "https://freysmedia.de",
        "url": "https://freysmedia.de",
        "telephone": "",
        "priceRange": "$$",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "",
          "addressLocality": "Villingen-Schwenningen",
          "addressRegion": "Baden-Württemberg",
          "postalCode": "",
          "addressCountry": "DE"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 48.0603,
          "longitude": 8.4614
        },
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": [
            "Monday",
            "Tuesday", 
            "Wednesday",
            "Thursday",
            "Friday"
          ],
          "opens": "09:00",
          "closes": "18:00"
        },
        "sameAs": [
          "https://www.instagram.com/freysmedia",
          "https://freycreate.ai"
        ]
      },
      {
        "@type": "SoftwareApplication",
        "name": "Freycreate.ai",
        "applicationCategory": "BusinessApplication",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "EUR"
        },
        "description": "All-in-One KI-Kreativstudio für Marketing-Content: Image Generation, Video Generation, Character Creation, Logo Builder und mehr.",
        "url": "https://freycreate.ai",
        "creator": {
          "@type": "Person",
          "name": "Kenny Frey"
        },
        "featureList": [
          "AI Image Generation (Gemini Nano Banana, Flux 2.0, Imagen)",
          "AI Video Generation (Veo 3.1, Kling 2.6, Seedance 1.5)",
          "Character Creator",
          "Logo Builder",
          "Ad Builder",
          "Brand Studio"
        ]
      }
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:site_name" content="Freys Media" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Additional SEO Tags */}
      <meta name="author" content="Kenny Frey" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Geo Tags for Local SEO */}
      <meta name="geo.region" content="DE-BW" />
      <meta name="geo.placename" content="Villingen-Schwenningen" />
      <meta name="geo.position" content="48.0603;8.4614" />
      <meta name="ICBM" content="48.0603, 8.4614" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
